import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../contexts/MemberContext';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import MemberHeader from '../components/MemberHeader';
import WelcomeSection from '../components/WelcomeSection';
import OffersScroller from '../components/OffersScroller';
import OfferRedemptionModal from '../components/OfferRedemptionModal';
import deviceService from '../services/deviceService';


/**
 * Member Dashboard Page
 * Shows member information, point balance, and available coupons
 */
const MemberDashboardPage = () => {
  const navigate = useNavigate();
  const { member, orientation, setSelectedOffer } = useMember();
  const [allOffers, setAllOffers] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedModalOffer, setSelectedModalOffer] = useState(null);

  useEffect(() => {
    if (orientation === -1) {
      // the device was flipped over so navigate to the cashier login page.
      navigate('/cashier-login');
    } else {
      const fetchCoupons = async () => {
        if (member && member.id) {
          setIsLoadingCoupons(true);
          try {
            const response = await deviceService.getAvailableCoupons(member.id);
            setAllOffers(response);
            if (response.length === 0) {
              // No coupons available, navigate to confirmation page
              navigate('/member-confirmation');
            }
          } catch (error) {
            console.error('Error fetching coupons:', error);
            // Keep empty arrays on error
            setAllOffers([]);
            navigate('/member-confirmation');
          } finally {
            setIsLoadingCoupons(false);
          }
        }
      };

      fetchCoupons();
    }
  }, [orientation]);

  // Redirect based on member status
  useEffect(() => {
    if (member) {
      if (member.is_optout) {
        navigate('/account-opted-out');
      } else if (!member.email_verified || !member.phone_verified) {
        navigate('/account-verification-pending');
      }
    }
  }, [member, navigate]);

  // Handle navigation to member confirmation
  const handleConfirmClick = () => {
    navigate('/member-confirmation');
  };

  // Handle offer click to open modal
  const handleOfferClick = (offer) => {
    setSelectedModalOffer(offer);
    setModalOpen(true);
  };

  // Handle redeem from modal
  const handleModalRedeem = () => {
    setSelectedOffer(selectedModalOffer);
    setModalOpen(false);
    navigate('/member-confirmation');
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedModalOffer(null);
  };



  return (
    <Container sx={{
      py: 4,
      height: '100%',
      width: '100%',
      px: '0 !important',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {/* Navigation header */}
      <MemberHeader />

      {/* Welcome section */}
      <WelcomeSection />


      {/* Offers horizontal scroll section */}
      {isLoadingCoupons ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            px: 3
          }}
        >
          <Typography variant="h6" color="text.secondary" align="center">
            Loading available coupons...
          </Typography>
        </Box>
      ) : allOffers.length > 0 ? (
        <OffersScroller
          offers={allOffers}
          memberPoints={member.current_points}
          onOfferClick={handleOfferClick}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            px: 3
          }}
        >
          <Typography variant="h6" color="text.secondary" align="center">
            No coupons are currently available. Check back soon for new offers!
          </Typography>
        </Box>
      )}

      {/* Actions section */}
      <Box mb={4} display="flex" justifyContent="center" gap={2}>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={handleConfirmClick}
        >
          Earn rewards without a coupon
        </Button>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>

      {/* Offer Redemption Modal */}
      <OfferRedemptionModal
        open={modalOpen}
        onClose={handleModalClose}
        offer={selectedModalOffer}
        memberPoints={member?.current_points || 0}
        onRedeem={handleModalRedeem}
      />

    </Container>
  );
};

export default MemberDashboardPage;